"use client";

import { updateProfile } from "deso-protocol";
import { useContext, useMemo, useState } from "react";

import { waitForTxn } from "@/backend/api-helpers";
import { UserContext } from "@/contexts/user-context";
import {
  DEFAULT_CREATOR_BASIS_POINTS,
  DEFAULT_STAKE_MULTIPLE_BASIS_POINTS,
  MIN_FEE_RATE_NANOS_PER_KB,
} from "@/utils/constants";
import {
  type FeedVisibilityPreferences,
  type FocusExtraDataV1,
  type FocusPreferencesSanitizeOptions,
  getFocusExtraDataString,
  parseFocusPreferences,
  sanitizeFocusPreferences,
  serializeFocusPreferences,
} from "@/utils/focus-preferences";
import {
  coerceProfileExtraData,
  sanitizeProfileExtraData,
} from "@/utils/profile-extra-data";

const LEGACY_TEMP_FEED_FILTERS_STORAGE_KEY = "tempFeedFilters_v2";
const PROFILE_REFRESH_ATTEMPTS = 8;
const PROFILE_REFRESH_DELAY_MS = 750;

type FocusPreferencesSavePhase =
  | "prepare-profile-payload"
  | "submit-profile-update"
  | "wait-for-profile-txn"
  | "refresh-saved-profile";

const logFocusPreferencesSaveFailure = ({
  availableCustomFeedIdsCount,
  availableListIdsCount,
  currentUserPublicKey,
  error,
  outgoingExtraDataBytes,
  phase,
  preferences,
  serializedFocusBytes,
}: {
  availableCustomFeedIdsCount: number;
  availableListIdsCount: number;
  currentUserPublicKey: string;
  error: unknown;
  outgoingExtraDataBytes: number;
  phase: FocusPreferencesSavePhase;
  preferences: FocusExtraDataV1;
  serializedFocusBytes: number;
}) => {
  const context = {
    phase,
    currentUserPublicKey,
    feedOrderCount: preferences.feedOrder.length,
    customFeedsCount: Object.keys(preferences.customFeeds).length,
    listMetadataCount: Object.keys(preferences.listMetadata).length,
    availableListIdsCount,
    availableCustomFeedIdsCount,
    serializedFocusBytes,
    outgoingExtraDataBytes,
  };

  console.error("Failed to save feed preferences", {
    ...context,
    error,
  });

  try {
    if (typeof dd !== "undefined") {
      dd.logger.error("Failed to save feed preferences", {
        ...context,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }
  } catch {
    // Datadog is a best-effort diagnostic path.
  }
};

const readLegacyTempFilters = (): Partial<FeedVisibilityPreferences> | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(
      LEGACY_TEMP_FEED_FILTERS_STORAGE_KEY,
    );

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<FeedVisibilityPreferences>;

    return {
      hideViewed:
        typeof parsed.hideViewed === "boolean" ? parsed.hideViewed : undefined,
      hideNSFW:
        parsed.hideNSFW === "hide" ||
        parsed.hideNSFW === "blur" ||
        parsed.hideNSFW === "show"
          ? parsed.hideNSFW
          : undefined,
      useModeration:
        typeof parsed.useModeration === "boolean"
          ? parsed.useModeration
          : undefined,
    };
  } catch {
    return null;
  }
};

const waitForSavedFocusPreferences = async ({
  expectedFocus,
  refetchUser,
}: {
  expectedFocus: string;
  refetchUser: () => void;
}) => {
  for (let attempt = 0; attempt < PROFILE_REFRESH_ATTEMPTS; attempt += 1) {
    const result = await (
      refetchUser as unknown as () => Promise<{
        data?: {
          accountByPublicKey?: {
            extraData?: unknown;
          } | null;
        } | null;
      }>
    )();
    const refetchedFocus = getFocusExtraDataString(
      coerceProfileExtraData(result?.data?.accountByPublicKey?.extraData),
    );

    if (refetchedFocus === expectedFocus) {
      return;
    }

    if (attempt < PROFILE_REFRESH_ATTEMPTS - 1) {
      await new Promise((resolve) => {
        window.setTimeout(resolve, PROFILE_REFRESH_DELAY_MS);
      });
    }
  }

  throw new Error(
    "Saved profile data is still stale after refresh. Please try again.",
  );
};

export const useFocusPreferences = () => {
  const { currentUserPublicKey, loggedIn, refetchUser, user } =
    useContext(UserContext);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const profileExtraData = useMemo(
    () => coerceProfileExtraData(user?.extraData),
    [user?.extraData],
  );
  const focusExtraData = useMemo(() => {
    return getFocusExtraDataString(profileExtraData);
  }, [profileExtraData]);

  const preferences = useMemo(() => {
    return parseFocusPreferences(focusExtraData, {
      loggedIn,
      seedPreferences: readLegacyTempFilters(),
    });
  }, [focusExtraData, loggedIn]);

  const savePreferences = async (
    nextPreferences: FocusExtraDataV1,
    options: FocusPreferencesSanitizeOptions,
  ) => {
    if (!currentUserPublicKey || !user) {
      throw new Error("A logged-in user is required to save feed preferences.");
    }

    setSaving(true);
    setError(null);

    const sanitizedPreferences = sanitizeFocusPreferences(
      nextPreferences,
      options,
    );
    const serializedFocusPreferences = serializeFocusPreferences(
      sanitizedPreferences,
      options,
    );

    let savePhase: FocusPreferencesSavePhase = "prepare-profile-payload";
    let nextExtraData: Record<string, string> | null = null;

    try {
      const baseExtraData = sanitizeProfileExtraData(profileExtraData);
      const username = user.username ?? "";
      const displayName = baseExtraData.DisplayName || username;

      savePhase = "submit-profile-update";
      nextExtraData = {
        ...(displayName ? { DisplayName: displayName } : {}),
        focus: serializedFocusPreferences,
      };
      const response = await updateProfile({
        ProfilePublicKeyBase58Check: currentUserPublicKey,
        UpdaterPublicKeyBase58Check: currentUserPublicKey,
        NewUsername: username,
        NewDescription: user.description ?? "",
        NewCreatorBasisPoints: user.creatorBasisPoints
          ? Number(user.creatorBasisPoints)
          : DEFAULT_CREATOR_BASIS_POINTS,
        NewStakeMultipleBasisPoints: DEFAULT_STAKE_MULTIPLE_BASIS_POINTS,
        MinFeeRateNanosPerKB: MIN_FEE_RATE_NANOS_PER_KB,
        IsHidden: false,
        ExtraData: nextExtraData,
      });

      const transactionHash =
        response.submittedTransactionResponse?.TxnHashHex ?? null;

      if (!transactionHash) {
        throw new Error("Transaction was not constructed. Please try again.");
      }

      savePhase = "wait-for-profile-txn";
      await waitForTxn({
        transactionHash,
      });

      savePhase = "refresh-saved-profile";
      await waitForSavedFocusPreferences({
        expectedFocus: serializedFocusPreferences,
        refetchUser,
      });
    } catch (nextError) {
      logFocusPreferencesSaveFailure({
        availableCustomFeedIdsCount: Array.from(
          options.availableCustomFeedIds ?? [],
        ).length,
        availableListIdsCount: Array.from(options.availableListIds ?? [])
          .length,
        currentUserPublicKey,
        error: nextError,
        outgoingExtraDataBytes: Buffer.byteLength(
          JSON.stringify(nextExtraData ?? {}),
          "utf8",
        ),
        phase: savePhase,
        preferences: sanitizedPreferences,
        serializedFocusBytes: Buffer.byteLength(
          serializedFocusPreferences,
          "utf8",
        ),
      });

      const finalError =
        nextError instanceof Error
          ? new Error(
              `Failed to save feed preferences during ${savePhase}. ${nextError.message}`,
            )
          : new Error(`Failed to save feed preferences during ${savePhase}.`);

      setError(finalError);
      throw finalError;
    } finally {
      setSaving(false);
    }
  };

  return {
    preferences,
    savePreferences,
    saving,
    error,
    hasStoredPreferences: !!focusExtraData,
  };
};
